import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import keyBy from 'lodash/keyBy';
import groupBy from 'lodash/groupBy';
import { put, fork, take, select } from 'redux-saga/effects';
import * as actions from './actions';


const SIMPLE_ITEM_TYPE = 2;

function getItemOptions({ items, boardOption, itemOptions, simpleOptions, containerOptions }) {
  if (boardOption.isGroupedBoard) {
    const item = find(items, x => x.id === boardOption.value.columns[0].config && boardOption.value.columns[0].config[0].id);
    if (item) {
      const options = (item.containerType === SIMPLE_ITEM_TYPE ? simpleOptions : containerOptions).filter(x => x.id !== item.id);
      return options;
    }
  }
  return itemOptions;
}

function getFreeItemType(config, itemOptions) {
  const itemMap = keyBy(config, 'id');
  const item = find(itemOptions, x => !itemMap[x.id]);
  return item && item.id;
}

function getColumnOption(value, itemOptions, groupedOptions) {
  const itemMap = keyBy(value.config, 'id');
  let configOptions = [];
  const groupedOptionsToExclude = [];
  if (value.isGrouped) {
    value.config.forEach(x => groupedOptionsToExclude.push(x.id));
    configOptions = value.config.map(x => ({
      value: x,
      id: x.id,
      itemOptions: [find(groupedOptions, y => y.id === x.id), ...groupedOptions.filter(y => !groupedOptionsToExclude.includes(y.id))],
    }));
  } else {
    configOptions = value.config.map(x => ({
      value: x,
      id: x.id,
      itemOptions: [find(itemOptions, y => y.id === x.id), ...itemOptions.filter(y => !itemMap[y.id])],
    }));
  }
  return {
    value,
    configOptions,
  };
}

function getColumnOptions(columns, itemOptions, groupedOptions) {
  let id = 0;
  let order = 0;
  return columns.map((x) => {
    const columnValue = cloneDeep(x);
    return getColumnOption({
      ...columnValue,
      id: ++id,
      order: ++order,
    }, itemOptions, groupedOptions);
  });
}

function* init() {
  while (true) {
    const { localId } = yield take(actions.INIT.BEGIN);
    const { boardOptions, items, itemOptions, simpleOptions, containerOptions, groupedOptions } = yield select(state => state.configuration.project.editor.board);
    const selectedBorad = cloneDeep(find(boardOptions, x => x.$$localId === localId) || { invalid: true, value: {} });
    const groupedColumn = selectedBorad.value.columns.filter(x => x.isGrouped)[0];
    const selectedValue = {
      ...selectedBorad,
      isGroupedBoard: !!groupedColumn,
    };
    const columnOptions = getColumnOptions(selectedBorad.value.columns,
      getItemOptions({
        items,
        itemOptions,
        simpleOptions,
        containerOptions,
        boardOption: selectedValue,
      }), groupedOptions);
    yield put(actions.init.end({ selectedValue, columnOptions }));
  }
}

function* setIsGroupedBoard() {
  while (true) {
    const { value } = yield take(actions.SET_IS_GROUPED_BOARD.BEGIN);
    const {
      currentBoard: selectedBoard,
      itemOptions,
      groupedOptions,
    } = yield select(state => state.configuration.project.editor.board);
    const columns = value ? [{
      ...selectedBoard.value.columns[0],
      isGrouped: true,
    }, ...selectedBoard.value.columns.slice(1)] : selectedBoard.value.columns.map(col => ({...col, isGrouped: false}));
    const columnOptions = getColumnOptions(columns,
      getItemOptions({
        itemOptions,
        boardOption: selectedBoard,
      }), groupedOptions);
    yield put(actions.setIsGroupedBoard.end({
      columns,
      columnOptions,
    }));
  }
}

function* addColumn() {
  while (true) {
    yield take(actions.ADD_COLUMN.BEGIN);
    const {
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      currentBoard: selectedBoard,
      groupedOptions,
       } = yield select(state => state.configuration.project.editor.board);
    const columns = selectedBoard.columnOptions.map(x => x.value);
    columns.push({
      name: '',
      config: [],
    });
    const columnOptions = getColumnOptions(columns, getItemOptions({
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      boardOption: selectedBoard,
    }), groupedOptions);
    yield put(actions.addColumn.end({
      columnOptions,
    }));
  }
}

function* removeColumn() {
  while (true) {
    const { columnId } = yield take(actions.REMOVE_COLUMN.BEGIN);
    const {
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      currentBoard: selectedBoard,
      groupedOptions,
       } = yield select(state => state.configuration.project.editor.board);
    const columns = selectedBoard.columnOptions.filter(x => x.value.id !== columnId).map(x => x.value);
    const columnOptions = getColumnOptions(columns, getItemOptions({
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      boardOption: selectedBoard,
    }), groupedOptions);
    yield put(actions.removeColumn.end({
      columnOptions,
    }));
  }
}

function* addRow() {
  while (true) {
    const { columnId } = yield take(actions.ADD_ROW.BEGIN);
    const {
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      currentBoard: selectedBoard,
      groupedOptions,
       } = yield select(state => state.configuration.project.editor.board);
    const currentOptions = getItemOptions({
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      boardOption: selectedBoard,
    });
    const highlights = {};
    const columnOptions = selectedBoard.columnOptions.map((x) => {
      if (x.value.id === columnId) {
        const itemTypeId = getFreeItemType(x.value.config, currentOptions);
        const statusId = 1;
        if (itemTypeId) {
          highlights[`${columnId}:${itemTypeId}:${statusId}`] = true;
          return getColumnOption({
            ...x.value,
            config: [
              ...x.value.config,
              { id: itemTypeId, statusId, isProgressColumn: false, notHaveProgressColumnInFutureFlow: false },
            ],
          }, currentOptions, groupedOptions);
        }
      }
      return x;
    });
    yield put(actions.addRow.end({
      columnOptions,
      highlights
    }));
  }
}

function* removeRow() {
  while (true) {
    const { columnId, itemTypeConfigId } = yield take(actions.REMOVE_ROW.BEGIN);
    const {
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      currentBoard: selectedBoard,
      groupedOptions
       } = yield select(state => state.configuration.project.editor.board);
    const currentOptions = getItemOptions({
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      boardOption: selectedBoard,
    });
    const columnOptions = selectedBoard.columnOptions.map((x) => {
      if (x.value.id === columnId) {
        return getColumnOption({
          ...x.value,
          config: x.value.config.filter(y => y.id !== itemTypeConfigId),
        }, currentOptions, groupedOptions);
      }
      return x;
    });
    yield put(actions.removeRow.end({
      columnOptions,
    }));
  }
}

function* setItemType() {
  while (true) {
    const { columnId, curentItemTypeConfigId, newItemTypeConfigId } = yield take(actions.SET_ITEM_TYPE.BEGIN);
    const {
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      currentBoard: selectedBoard,
       } = yield select(state => state.configuration.project.editor.board);
    const currentOptions = getItemOptions({
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      boardOption: selectedBoard,
    });
    const highlights = {};
    const columnOptions = selectedBoard.columnOptions.map((x) => {
      if (x.value.id === columnId) {
        return getColumnOption({
          ...x.value,
          config: x.value.config.map((y) => {
            if (y.id === curentItemTypeConfigId) {
              highlights[`${columnId}:${newItemTypeConfigId}:${y.statusId}`] = true;
              return {
                ...y,
                id: newItemTypeConfigId,
              };
            }
            return y;
          }),
        }, currentOptions);
      }
      return x;
    });
    yield put(actions.setItemType.end({
      columnOptions,
      highlights,
    }));
  }
}

function* setStatus() {
  while (true) {
    const { columnId, itemTypeConfigId, statusId } = yield take(actions.SET_STATYS.BEGIN);
    const {
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      currentBoard: selectedBoard,
       } = yield select(state => state.configuration.project.editor.board);
    const currentOptions = getItemOptions({
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      boardOption: selectedBoard,
    });
    const highlights = {};
    const columnOptions = selectedBoard.columnOptions.map((x) => {
      if (x.value.id === columnId) {
        return getColumnOption({
          ...x.value,
          config: x.value.config.map((y) => {
            if (y.id === itemTypeConfigId) {
              highlights[`${columnId}:${y.id}:${statusId}`] = true;
              return {
                ...y,
                statusId,
              };
            }
            return y;
          }),
        }, currentOptions);
      }
      return x;
    });
    yield put(actions.setStatus.end({
      columnOptions,
      highlights,
    }));
  }
}

function* setColumnName() {
  while (true) {
    const { columnId, name } = yield take(actions.SET_COLUMN_NAME.BEGIN);
    const {
      currentBoard: selectedBoard,
       } = yield select(state => state.configuration.project.editor.board);
    const columnOptions = selectedBoard.columnOptions.map((x) => {
      if (x.value.id === columnId) {
        return {
          ...x,
          value: {
            ...x.value,
            name,
          },
        };
      }
      return x;
    });
    yield put(actions.setColumnName.end({
      columnOptions,
    }));
  }
}

function* setGroupItemType() {
  while (true) {
    const { oldItemTypeConfigId, newItemTypeConfigId } = yield take(actions.SET_GROUP_ITEM_TYPE.BEGIN);
    const {
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      currentBoard: selectedBoard,
      groupedOptions,
       } = yield select(state => state.configuration.project.editor.board);
    const currentOptions = getItemOptions({
      items,
      itemOptions,
      simpleOptions,
      containerOptions,
      boardOption: selectedBoard,
    });
    const item = find(items, x => x.id === newItemTypeConfigId);
    const restOfConfig = selectedBoard.value.columns[0].config.filter(c => c.id !== oldItemTypeConfigId);

    const groupedColumn = {
      ...selectedBoard.value.columns[0],
      //name: item.name,
      config: [
        ...restOfConfig,
        { issueType: newItemTypeConfigId },
      ],
    };
   const columnOptions = getColumnOptions(selectedBoard.value.columns.map(x => x.isGrouped ? groupedColumn : {
     ...x,
    }), currentOptions, groupedOptions);
    yield put(actions.setGroupItemType.end({
      columnOptions,
    }));
  }
}

function* checkValidation() {
  while (true) {
    yield take(actions.CHECK_VALIDATION.BEGIN);
    const { currentBoard: selectedBoard, saving, boards } = yield select(state => state.configuration.project.editor.board);

      let errors;
      if (saving || selectedBoard.changes) {
          if (!selectedBoard.columnOptions || !selectedBoard.columnOptions.length ||
              find(selectedBoard.columnOptions, x => !x.configOptions || !x.configOptions.length || !x.value.name) ||
        selectedBoard.isGroupedBoard && !selectedBoard.value.columns[0].config.length
      ) {
        errors = {table: {}};
      } else {
        const configs = selectedBoard.columnOptions.reduce((acc, val) => [...acc, ...val.configOptions], []);
        const configGroups = groupBy(configs, x => `${x.value.id}:${x.value.statusId}`);
        const conf = find(Object.keys(configGroups), x => configGroups[x].length > 1);
        if (conf) {
          errors = {
            tableConfig: {
              [conf]: true,
            }
          };
        }
      }

      if (boards.find(board => board.name === selectedBoard.value.name && board.id !== selectedBoard.value.id)) {
        errors = {name: {}, ...errors};
      }
    }

    yield put(actions.checkValidation.end({ errors }));
  }
}


export default function* currentBoard() {
  yield fork(init);
  yield fork(setIsGroupedBoard);
  yield fork(addColumn);
  yield fork(removeColumn);
  yield fork(addRow);
  yield fork(removeRow);
  yield fork(setItemType);
  yield fork(setStatus);
  yield fork(setColumnName);
  yield fork(setColumnName);
  yield fork(setGroupItemType);
  yield fork(checkValidation);
}



// WEBPACK FOOTER //
// ../redux/store/configuration/project/editor/board/currentBoard/saga.js